/**
 * Shared chart configuration for rich, immersive charts
 */

/**
 * Creates a gradient fill for line charts
 */
export const createGradient = (ctx, colorStart, colorEnd) => {
  const gradient = ctx.createLinearGradient(0, 0, 0, 300);
  gradient.addColorStop(0, colorStart);
  gradient.addColorStop(1, colorEnd);
  return gradient;
};

/**
 * Color palettes for charts
 */
export const chartColors = {
  violet: {
    primary: '#581c87',
    secondary: '#7c3aed',
    light: '#a78bfa',
    gradientStart: 'rgba(88, 28, 135, 0.4)',
    gradientEnd: 'rgba(88, 28, 135, 0.0)',
  },
  blue: {
    primary: '#1e3a8a',
    secondary: '#3b82f6',
    light: '#93c5fd',
    gradientStart: 'rgba(30, 58, 138, 0.4)',
    gradientEnd: 'rgba(30, 58, 138, 0.0)',
  },
  purple: {
    primary: '#670199',
    secondary: '#9333ea',
    light: '#c084fc',
    gradientStart: 'rgba(103, 1, 153, 0.35)',
    gradientEnd: 'rgba(103, 1, 153, 0.0)',
  },
  accent: {
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
  },
  stacked: {
    primary: '#7c3aed',
    secondary: '#4b127c',
    tertiary: '#a78bfa',
  }
};

/**
 * Rich tooltip configuration for Chart.js 4
 */
export const richTooltipConfig = {
  enabled: true,
  backgroundColor: 'rgba(255, 255, 255, 0.96)',
  titleColor: '#1f2937',
  titleFont: {
    size: 13,
    weight: '600',
    family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  bodyColor: '#6b7280',
  bodyFont: {
    size: 12,
    family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  borderColor: 'rgba(88, 28, 135, 0.2)',
  borderWidth: 1,
  padding: 12,
  cornerRadius: 8,
  displayColors: true,
  boxPadding: 4,
  usePointStyle: true,
  caretSize: 6,
  caretPadding: 8,
};

/**
 * Animation configuration for smooth, engaging transitions
 */
export const richAnimationConfig = {
  duration: 750,
  easing: 'easeOutQuart',
  delay: (context) => context.dataIndex * 30,
};

/**
 * Crosshair/tracking line plugin
 */
export const crosshairPlugin = {
  id: 'crosshair',
  afterDatasetsDraw: (chart) => {
    if (chart.tooltip?._active?.length) {
      const activePoint = chart.tooltip._active[0];
      const ctx = chart.ctx;
      const x = activePoint.element.x;
      const topY = chart.scales.y.top;
      const bottomY = chart.scales.y.bottom;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, topY);
      ctx.lineTo(x, bottomY);
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(88, 28, 135, 0.15)';
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.restore();
    }
  },
};

/**
 * Hover glow effect plugin
 */
export const glowPlugin = {
  id: 'glow',
  beforeDatasetsDraw: (chart) => {
    const ctx = chart.ctx;
    ctx.save();
    ctx.shadowColor = 'rgba(88, 28, 135, 0.4)';
    ctx.shadowBlur = 12;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 4;
  },
  afterDatasetsDraw: (chart) => {
    chart.ctx.restore();
  },
};

/**
 * Creates base chart options with rich styling
 */
export const createRichChartOptions = (options = {}) => {
  const {
    showGrid = true,
    yTickCallback,
    xTickCallback,
    maxYTicks = 6,
    maxXTicks = 6,
  } = options;

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    hover: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: richTooltipConfig,
    },
    animation: richAnimationConfig,
    scales: {
      y: {
        display: true,
        grid: {
          display: showGrid,
          color: 'rgba(148, 163, 184, 0.1)',
          drawBorder: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: '#94a3b8',
          font: {
            size: 11,
            family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          },
          padding: 12,
          maxTicksLimit: maxYTicks,
          callback: yTickCallback,
        },
      },
      x: {
        display: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: '#94a3b8',
          font: {
            size: 10,
            family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          },
          padding: 8,
          maxTicksLimit: maxXTicks,
          maxRotation: 0,
          callback: xTickCallback,
        },
      },
    },
  };
};

/**
 * Creates a rich line dataset configuration
 */
export const createRichLineDataset = (ctx, data, options = {}) => {
  const {
    label = 'Dataset',
    borderColor = chartColors.purple.primary,
    gradientStart = chartColors.purple.gradientStart,
    gradientEnd = chartColors.purple.gradientEnd,
    tension = 0.35,
  } = options;

  const gradient = createGradient(ctx, gradientStart, gradientEnd);

  return {
    label,
    data,
    fill: true,
    backgroundColor: gradient,
    borderColor,
    borderWidth: 2.5,
    tension,
    pointRadius: 0,
    pointHoverRadius: 6,
    pointHoverBackgroundColor: '#fff',
    pointHoverBorderColor: borderColor,
    pointHoverBorderWidth: 2.5,
    pointHitRadius: 20,
  };
};

/**
 * Creates a rich bar dataset configuration
 */
export const createRichBarDataset = (data, options = {}) => {
  const {
    label = 'Dataset',
    backgroundColor = chartColors.stacked.primary,
    borderRadius = 4,
    hoverBackgroundColor,
  } = options;

  return {
    label,
    data,
    backgroundColor,
    borderRadius,
    borderSkipped: false,
    hoverBackgroundColor: hoverBackgroundColor || backgroundColor,
    barPercentage: 0.7,
    categoryPercentage: 0.8,
  };
};
